import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import MapView from "@/components/MapView";
import { api, LOGO_URL, HERO_VIDEO_URL } from "@/lib/api";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function Home() {
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [markers, setMarkers] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    api.get("/markers").then((r) => setMarkers(r.data)).catch(() => {});
  }, []);

  const statItems = [
    { label: "Tracked locations", value: stats?.tracked_locations ?? "—" },
    { label: "Confirmed sites", value: stats?.confirmed ?? "—" },
    { label: "Under development", value: stats?.under_development ?? "—" },
    { label: "Research articles", value: stats?.articles ?? "—" },
    { label: "Last updated", value: stats?.last_updated ? fmtDate(stats.last_updated) : "—", small: true },
    { label: "Archive year", value: stats?.archive_year ?? "2025" },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B0F17] text-white dcep-grain">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(60% 80% at 75% 30%, rgba(2,132,199,0.35), transparent), radial-gradient(50% 60% at 85% 70%, rgba(5,150,105,0.3), transparent)" }} />
        <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 pt-12 pb-20 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl order-2 lg:order-1">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live research archive · New Zealand
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Following the <em className="text-[#2E8BFF] not-italic font-medium">infrastructure</em> behind Data Centres in New Zealand.
            </h1>
            <p className="mt-7 text-lg text-white/70 leading-relaxed max-w-2xl font-sans">
              DCEPtNZ is an independent research project tracking how data centres, energy systems and international connections are reshaping New Zealand.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/map" className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0B0F17] font-medium text-sm hover:bg-emerald-400 transition-colors" data-testid="hero-explore-map">
                <MapPin size={16} /> Explore the map
              </Link>
              <Link to="/research" className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/60 transition-colors" data-testid="hero-read-latest">
                Read the latest <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </motion.div>
          <motion.video initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }}
            src={HERO_VIDEO_URL} poster={LOGO_URL} autoPlay muted loop playsInline
            className="order-1 lg:order-2 w-full max-w-[280px] sm:max-w-[340px] xl:max-w-[400px] mx-auto lg:mx-0 aspect-square object-cover rounded-2xl ring-1 ring-white/10 shadow-[0_30px_80px_rgba(46,139,255,0.25)]" data-testid="hero-video" />
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 rounded-xl overflow-hidden border border-white/10">
            {statItems.map((s, i) => (
              <div key={i} className="bg-[#0B0F17] p-5 lg:p-6" data-testid={`home-stat-${i}`}>
                <div className={`font-serif text-white ${s.small ? "text-xl lg:text-2xl leading-tight pt-1.5 lg:pt-3" : "text-3xl lg:text-4xl"}`}>{s.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-start">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#059669] mb-5">The project</div>
          <h2 className="font-serif text-3xl lg:text-4xl leading-tight text-[#11181C]">
            A clear view of a <em className="not-italic text-[#2E8BFF] font-medium">quiet transformation.</em>
          </h2>
        </div>
        <div>
          <p className="text-lg text-[#52606D] leading-relaxed">
            New Zealand's digital future is being built in places most people never see. We bring the planning documents, grid connections, cable routes and local stories into one public record.
          </p>
          <Link to="/map" className="mt-6 inline-flex items-center gap-2 text-[#11181C] font-medium dcep-link-underline" data-testid="intro-see-tracking">
            See what we're tracking <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Map preview */}
      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-20">
        <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm relative">
          <MapView markers={markers} posts={posts} height="460px" zoom={5} interactive={false} />
          <Link to="/map" className="absolute bottom-5 right-5 z-[400] inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0B0F17] text-white text-sm font-medium shadow-lg hover:bg-[#1b2230]" data-testid="mappreview-open">
            Open full map <ArrowUpRight size={15} />
          </Link>
        </div>
      </section>

      {/* Latest research */}
      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-24">
        <div className="flex items-end justify-between mb-10 border-b border-slate-200 pb-5">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#059669] mb-2">From the archive</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-[#11181C]">Latest research</h2>
          </div>
          <Link to="/research" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#11181C] dcep-link-underline">
            View all research <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
          {posts.map((p, i) => (
            <Link key={p.id} to={`/research/${p.slug}`} className="group bg-white p-7 hover:bg-slate-100 transition-colors flex flex-col" data-testid={`home-post-${i}`}>
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.12em] text-[#52606D]">
                <span className="text-[#059669]">{p.category}</span>
                <span>·</span>
                <span>{fmtDate(p.published_at)}</span>
              </div>
              <h3 className="font-serif text-xl text-[#11181C] mt-4 leading-snug group-hover:text-[#2E8BFF] transition-colors">{p.title}</h3>
              <p className="text-sm text-[#52606D] mt-3 leading-relaxed flex-1">{p.excerpt}</p>
              <div className="mt-5 font-mono text-[11px] text-[#52606D]">{p.read_minutes || 5} min read</div>
            </Link>
          ))}
          {posts.length === 0 && <div className="bg-white p-10 text-center text-[#52606D] md:col-span-3">No published research yet.</div>}
        </div>
      </section>
    </Layout>
  );
}
